import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '../gql';
import React from 'react';
import I18n from 'i18n-js';
import track from 'react-tracking';
import { withUserContext, IUserContext } from './user_context_provider';
import { userIsTrackable } from '../elog/mparticle';
import { useEppoExpEnabled } from '../user_hooks';
import eppoFlags from '../eppoFlags.json';
import ListingsCollection from './listings_collection';
import { CommonsWatchedListings } from '../gql/graphql';
import { withGraphql } from '../with_graphql';
import { connect } from '../connect';
import { WatchedListingsFallback } from './watched_listings_fallback';

const COMPONENT_NAME = 'WatchRow';
const MIN_COUNT = 5;
const MAX_COUNT = 8;

export interface ExternalProps {
  mainCallToAction?: React.ReactElement;
  secondaryCallToAction?: React.ReactElement;
  showFlags?: boolean;
}

type IProps = ChildProps<IUserContext & ExternalProps, CommonsWatchedListings.Query>;

export function WatchedListings(props: IProps) {
  const { data: { me, loading } } = props;
  const isFavoritesRebrandEnabled = useEppoExpEnabled({ flagKey: eppoFlags.SAVED_SEARCH_FAVORITES_REBRAND });
  const limit = MAX_COUNT;
  const listings = (me?.watches?.nodes ?? []).map(n => n.listing).slice(0, limit);
  const isEmptyWatchlist = !loading && !listings.length;
  const title = I18n.t(isFavoritesRebrandEnabled ? 'commons.watchRow.loggedIn.savedTitle' : 'commons.watchRow.loggedIn.title');
  const minCount = MIN_COUNT;

  if (listings.length < minCount && !loading) {
    const topProductTypeUuids = me?.profile?.topProductTypeUuids || [];
    const topProductTypes = me?.profile?.topProductTypes || [];

    return (
      <WatchedListingsFallback
        emptyStateMainCallToAction={props.mainCallToAction}
        emptyStateSecondaryCallToAction={props.secondaryCallToAction}
        emptyStateTitleOverride={isFavoritesRebrandEnabled ? title : undefined}
        hasWatchedListings={!isEmptyWatchlist}
        topProductTypeUuid={topProductTypeUuids[0]}
        topProductTypeName={topProductTypes[0]}
      />
    );
  }

  return (
    <ListingsCollection
      listings={listings}
      loading={loading}
      callToAction={props.mainCallToAction}
      componentName={COMPONENT_NAME}
      title={title}
      indentedMosaic
      largeTiles={false}
      showFlags={props.showFlags}
    />
  );
}

const watchedListingsQuery = gql(`
  query Commons_WatchedListings(
    $limit: Int
    $shouldSkipTracking: Boolean!
  ) {
    me {
      _id
      uuid
      profile {
        topProductTypeUuids
        topProductTypes
      }
      watches(input: {
        limit: $limit
        listingsState: LIVE
      }) {
        nodes {
          _id
          listing {
            _id
            ...ListingsCollection
            ...mParticleListingFields @skip(if: $shouldSkipTracking)
          }
        }
      }
    }
  }
`);

function buildWatchedListingsVars(ownProps: IProps) {
  return {
    ssr: true,
    skip: !!ownProps.user?.loggedOut,
    variables: {
      limit: MAX_COUNT,
      shouldSkipTracking: !userIsTrackable(),
    },
  };
}

const queryLarge = withGraphql<IProps, CommonsWatchedListings.Query, CommonsWatchedListings.Variables>(
  watchedListingsQuery,
  {
    options: buildWatchedListingsVars,
  },
);

const WatchedListingsLarge = connect<ExternalProps>([
  withUserContext,
  queryLarge,
])(track({ componentName: COMPONENT_NAME })(WatchedListings));

export default (props: IProps) => {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <WatchedListingsLarge {...props} />;
};
